<template>
  <div id="verify-match-step-right-part"
       class="row col-12 col-md-8 d-flex justify-content-center justify-content-md-start pl-md-0 mt-3 mt-md-0 styled-scrollbar-custom pr-under-md-0 pl-under-md-0">
    <div id="verify-match-step-right-part-inside" class="w-100">

      <div class="d-flex align-items-center justify-content-center small-text">
        <span>{{ $t('verify.match.page.search.done') }}</span>
      </div>
      <div>
        <div class="card-plate mb-3">
          {{ identifier }}
        </div>
      </div>

      <div class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="addressInput"
          :invalid-feedback="address.invalidFeedback"
          :label="$t('annonce.place.robbery')"
          :state="address.state"
          class="small-text"
          label-for="address-input">
          <b-input-group>
            <GmapAutocomplete
              id="address-input"
              ref="addressAutocomplete"
              v-model="address.value"
              :options="autocompleteOptions"
              class="form-control"
              required
              @change="removeInvalidFeedback('address')"
              @input="handleStartAddressInput"
              @place_changed="handleAddressSelect">
            </GmapAutocomplete>
            <b-input-group-append>
              <div id="pin-locate"
                   :class="locationPermission === 'granted' ? 'custom-append-locate cursor-pointer' : 'custom-append-locate-disabled'"
                   @click="locateUser">
                <i :class="locationPermission === 'granted'  ? 'main-color' : 'text-disabled'"
                   class="ph-custom ph-map-pin"/>
              </div>
            </b-input-group-append>
            <b-tooltip v-if="locationPermission !== 'granted'" placement="bottom" target="pin-locate" triggers="hover">
              {{ $t('autorisation.geolocation.tooltip') }}
            </b-tooltip>
          </b-input-group>
        </b-form-group>
      </div>

      <div class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="vehicleTypeInput"
          :invalid-feedback="vehicleType.invalidFeedback"
          :label="$t('verify.match.vehicle.type')"
          :state="vehicleType.state"
          class="small-text"
          label-for="vehicleType-input">
          <b-form-select
            id="vehicleType-input"
            v-model="vehicleType.value"
            :state="vehicleType.state"
            required
            @change="updateVehicleTypeData(); removeInvalidFeedback('vehicleType')">
            <option v-for="vehicleType in vehicleTypes" :value="vehicleType.id">
              {{ vehicleType.name }}
            </option>
          </b-form-select>
        </b-form-group>
      </div>

      <AutocompleteField
        key="brandAutocomplete"
        ref="brandInput"
        v-model="brand.value"
        :initial-value="brand.value"
        :input-label="$t('VehicleBrand')"
        :invalidFeedback="brand.invalidFeedback"
        :items="brands"
        :need-create-if-empty="false"
        :ready-to-set-initial-value="readyToSetInitialBrand"
        :state="brand.state"
        class="col-12 pl-0 pr-0 mb-1"
        input-class="small-text mb-4"
        input-id="brand-input"
        @newSelectedValue="getSelectedBrand">
      </AutocompleteField>

      <AutocompleteField
        key="modelAutocomplete"
        ref="modelInput"
        v-model="model.value"
        :initial-value="model.value"
        :input-label="$t('VehicleModel')"
        :invalidFeedback="model.invalidFeedback"
        :items="models"
        :need-create-if-empty="false"
        :ready-to-set-initial-value="readyToSetInitialModel"
        :state="model.state"
        class="col-12 pl-0 pr-0 mb-1"
        input-class="small-text mb-4"
        input-id="model-input"
        @newSelectedValue="getSelectedModel">
      </AutocompleteField>

      <div v-if="isLicensedAndNeedVIN" class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="vinInput"
          :invalid-feedback="VIN.invalidFeedback"
          :label="$t('annonce.vehicle.VIN')"
          class="small-text"
          label-for="VIN-input">
          <b-form-input
            id="VIN-input"
            v-model="VIN.value"
            :formatter="IdentifierUtils.formatVin"
            :state="VIN.state"
            @change="setMatch;removeInvalidFeedback('VIN')">
          </b-form-input>
        </b-form-group>
      </div>

      <div class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="colorInput"
          :invalid-feedback="color.invalidFeedback"
          :label="$t('annonce.vehicle.Color')"
          :state="color.state"
          class="small-text"
          label-for="color-input">
          <b-form-select
            id="color-input"
            v-model="color.value"
            :state="color.state"
            required
            @change="setMatch;removeInvalidFeedback('color')">
            <option v-for="color in colors" :value="color.value">
              {{ $t(color.text) }}
            </option>
          </b-form-select>
        </b-form-group>
      </div>

      <AutocompleteField
        v-if="isLicensedAndNeedVIN"
        key="modelFinishAutocomplete"
        ref="modelFinishInput"
        v-model="modelFinish.value"
        :initial-value="modelFinish.value"
        :input-label="$t('vehicleModel.Finish')+ ' ' + $t('verify.match.optional')"
        :invalidFeedback="modelFinish.invalidFeedback"
        :items="modelFinishes"
        :label-is-html="true"
        :need-create-if-empty="false"
        :ready-to-set-initial-value="readyToSetInitialModelFinish"
        :state="modelFinish.state"
        class="col-12 pl-0 pr-0 mb-1"
        input-class="small-text mb-4"
        input-id="modelFinish-input"
        @newSelectedValue="getSelectedModelFinish">
      </AutocompleteField>

      <div class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="energyInput"
          :invalid-feedback="energy.invalidFeedback"
          :label="$t('annonce.vehicle.Energy')+ ' ' + $t('verify.match.optional')"
          :state="energy.state"
          class="small-text"
          label-for="energy-input">
          <b-form-select
            id="energy-input"
            v-model="energy.value"
            :state="energy.state"
            @change="setMatch;removeInvalidFeedback('energy')">
            <option v-for="energy in energies" :value="energy.value">
              {{ $t(energy.text) }}
            </option>
          </b-form-select>
        </b-form-group>
      </div>

      <div class="col-12 pl-0 pr-0 mb-1">
        <b-form-group
          ref="descriptionInput"
          :label="$t('annonce.vehicle.description')"
          class="small-text"
          label-for="description-vehicle-input">
          <b-form-textarea
            id="description-vehicle-input"
            v-model="description.value"
            :placeholder="$t('verify.match.page.description.placeholder')"
            :state="description.state"
            @change="setMatch">
          </b-form-textarea>
        </b-form-group>
      </div>

    </div>
  </div>
</template>
<script>
import api from "../../services/api";
import IdentifierUtils from "../../utils/IdentifierUtils";
import AddressUtils from "../../utils/AddressUtils";
import AutocompleteField from "../../components/b122/editFields/AutocompleteField";

export default {
  name: "VerifyMatchStep2",

  components: {
    AutocompleteField
  },

  emits: ["updateMatch", "updateNeedScroll"],

  props: {
    scannedIdentifier: Object,
    identifier: String,
    match: {
      required: true,
      type: Object
    },
    needScrollInvalidFeedback: {
      required: true,
      type: Boolean
    }
  },

  data() {
    return {
      IdentifierUtils: IdentifierUtils,

      locationPermission: null,
      vehicleTypes: [],
      brands: [],
      models: [],
      modelFinishes: [],
      energies: [],
      colors: [],

      mountedIsFinished: false,
      readyToSetInitialBrand: false,
      readyToSetInitialModel: false,
      readyToSetInitialModelFinish: false,

      autocompleteOptions: {
        componentRestrictions: {
          country: [
            'fr',
            this.$store.state.currentUserCountryCode
          ],
        },
      },

      isLicensed: false,
      hasVIN: false,

      address: {state: null, invalidFeedback: null, value: null, mandatory: true},
      vehicleType: {state: null, invalidFeedback: null, value: null, mandatory: true},
      brand: {state: null, invalidFeedback: null, value: null, mandatory: true},
      model: {state: null, invalidFeedback: null, value: null, mandatory: true},
      VIN: {state: null, invalidFeedback: null, value: null, mandatory: false},
      color: {state: null, invalidFeedback: null, value: null, mandatory: true},
      energy: {state: null, invalidFeedback: null, value: null, mandatory: false},
      modelFinish: {state: null, invalidFeedback: null, value: null, mandatory: false},
      description: {state: null, invalidFeedback: null, value: null, mandatory: true},
    }
  },


  watch: {
    needScrollInvalidFeedback: {
      handler(newVal) {
        if (newVal && newVal === true) {
          this.scrollToInvalidField()
        }
      }
    },
    'vehicleType.value': {
      async handler(newVal, oldVal) {
        var oldValIsLicensed = this.isLicensed
        var oldValHasVIN = this.hasVIN

        if (this.vehicleType.value) {
          await this.fetchVehicleTypeInfo()
        } else {
          this.isLicensed = null
          this.hasVIN = null
        }

        var oneVehicleTypePropertyHasChanged = (oldValIsLicensed !== this.isLicensed || oldValHasVIN !== this.hasVIN)

        if ((newVal !== oldVal || oneVehicleTypePropertyHasChanged) && this.mountedIsFinished) {
          this.brand.value = null
          this.brand.state = null
          this.brand.invalidFeedback = null
        }
      }
    },

    'brand.value': function (newVal, oldVal) {
      if (!this.mountedIsFinished) {
        return
      }

      if (newVal !== oldVal) {
        this.removeInvalidFeedback('brand')
        this.model.value = null
        this.model.state = null
        this.modelFinish.value = null
        this.modelFinish.state = null
        this.updateBrandData()
      }
    },

    'model.value': function (newVal, oldVal) {
      if (!this.mountedIsFinished) {
        return
      }

      if (newVal !== oldVal) {
        this.removeInvalidFeedback('model')
        this.modelFinish.value = null
        this.modelFinish.state = null
        this.updateModelData()
      }
    },

    'modelFinish.value': function (newVal, oldVal) {
      if (!this.mountedIsFinished) {
        return
      }

      if (newVal !== oldVal) {
        this.removeInvalidFeedback('modelFinish')
      }
    },

    'VIN.value': function (newVal) {
      if (newVal !== undefined) {
        if (newVal === null || newVal === "") {
          this.VIN.value = null
          this.VIN.state = null
          this.VIN.invalidFeedback = null
          return
        }
        const validVin = IdentifierUtils.validVinFormat(newVal)
        if (validVin) {
          this.VIN.value = validVin
          this.VIN.state = true
          this.VIN.invalidFeedback = null
        } else {
          this.VIN.state = false
          this.VIN.invalidFeedback = this.$i18n.t('annonce.vin.invalidFeedBack')
        }
      }
    },
  },


  async mounted() {
    this.setInitialValues()
    this.locateUser(false)
    await this.fetchVehicleTypes(true)
    await this.fetchVehicleTypeInfo(true)
    await this.fetchBrands(true)
    await this.fetchModels(true)
    await this.fetchModelFinishes(true)
    await this.fetchEnergies()
    await this.fetchColors()

    if ("permissions" in navigator) {
      navigator.permissions.query({name: 'geolocation'}).then(permission => {
        this.locationPermission = permission.state;
      });
    }

    this.mountedIsFinished = true
  },

  computed: {
    isLicensedAndNeedVIN() {
      return (this.isLicensed && this.hasVIN)
    }
  },

  methods: {

    setInitialValues() {
      this.address = this.match.address
      this.vehicleType = this.match.vehicleType
      this.brand = this.match.brand
      this.model = this.match.model
      this.VIN = this.match.VIN
      this.modelFinish = this.match.modelFinish
      this.energy = this.match.energy
      this.color = this.match.color
      this.description = this.match.description
    },

    setMatch() {
      this.match.address = this.address
      this.match.vehicleType = this.vehicleType
      this.match.brand = this.brand
      this.match.model = this.model
      this.match.VIN = this.VIN
      this.match.energy = this.energy
      this.match.color = this.color
      this.match.modelFinish = this.modelFinish
      this.match.description = this.description

      this.$emit('updateMatch', this.match)
    },

    //region fetch
    async fetchEnergies() {
      api
        .get(`/apiMobile/getEnergies`)
        .then(response => {
          this.energies = response.data
        })
    },
    async fetchColors() {
      api
        .get(`/apiMobile/getColors`)
        .then(response => {
          this.colors = response.data
        })
    },
    async fetchVehicleTypes(isFromInit = false) {
      if (!this.mountedIsFinished && !isFromInit) {
        return
      }
      await api
        .get('/vehicleType/getAllActivated')
        .then(response => {
          this.vehicleTypes = response.data
        })
    },
    async fetchVehicleTypeInfo(isFromInit = false) {
      if (!this.mountedIsFinished && !isFromInit) {
        return
      }
      if (this.vehicleType.value) {
        await api
          .get(`/vehicleType/getInfoVehicleType/${this.vehicleType.value}`)
          .then(response => {
            this.isLicensed = response.data.isLicensed
            this.hasVIN = response.data.hasVIN
          })
      }
    },
    async fetchBrands(isFromInit = false) {
      if (!this.mountedIsFinished && !isFromInit) {
        return
      }

      //set this.brands
      if (!this.vehicleType.value) {
        this.brands = []
      } else {
        await api({
          method: 'POST',
          url: `/brand/getAllActivatedByVehicleType/${this.vehicleType.value}`,
        }).then(response => {
          this.brands = response.data
        })

        this.readyToSetInitialBrand = true
      }
    },
    async fetchModels(isFromInit = false) {
      if (!this.mountedIsFinished && !isFromInit) {
        return
      }

      //set this.models
      if (!this.brand.value) {
        this.models = []
      } else {
        let data = new FormData()
        data.append('brandId', this.brand.value)
        data.append('vehicleTypeId', this.vehicleType.value)
        await api({
          method: 'POST',
          url: `/model/getAllActivatedByVehicleTypeAndBrand`,
          data: data
        }).then(response => {
          this.models = response.data
        })

        this.readyToSetInitialModel = true
      }
    },
    async fetchModelFinishes(isFromInit = false) {
      if (!this.mountedIsFinished && !isFromInit) {
        return
      }

      //set this.modelFinishes
      if (!this.model.value || !this.isLicensed) {
        this.modelFinishes = []
      } else {
        await api({
          method: 'POST',
          url: `/modelFinish/getAllActivatedForModel/${this.model.value}`,
        }).then(response => {
          this.modelFinishes = response.data
        })
      }
    },
    //endregion

    //region object modif
    async updateVehicleTypeData() {
      await this.fetchVehicleTypeInfo()
      await this.fetchBrands()
      await this.fetchModels()
      await this.fetchModelFinishes()
    },
    async updateBrandData() {
      await this.fetchModels()
      await this.fetchModelFinishes()
    },
    async updateModelData() {
      await this.fetchModelFinishes()
    },

    getSelectedBrand(data) {
      if (data !== null) {
        this.brand.value = data
      } else {
        this.brand.value = null
      }
      this.removeInvalidFeedback('brand')
      this.setMatch()
    },
    getSelectedModel(data) {
      if (data !== null) {
        this.model.value = data
      } else {
        this.model.value = null
      }
      this.removeInvalidFeedback('model')
      this.setMatch()
    },
    getSelectedModelFinish(data) {
      if (data !== null) {
        this.modelFinish.value = data
      } else {
        this.modelFinish.value = null
      }
      this.removeInvalidFeedback('modelFinish')
      this.setMatch()
    },
    //endregion

    //region address
    handleStartAddressInput(value) {
      if (value instanceof InputEvent) {
        this.address.value = value.target.value;
      } else {
        this.address.value = value;
      }
      this.address.state = null
      this.address.invalidFeedback = null
      this.setMatch()
    },

    handleAddressSelect(place) {
      if (place && place.formatted_address) {
        this.address.value = place.formatted_address
      }
    },

    scrollToInvalidField() {
      this.$nextTick(() => {
        const fields = [
          'addressInput',
          'vehicleTypeInput',
          'brandInput',
          'modelInput',
          'vinInput',
          'colorInput',
          'modelFinishInput',
          'energyInput',
          'descriptionInput'
        ];

        for (const field of fields) {
          const fieldRef = this.$refs[field];
          if (fieldRef && !fieldRef.state) {
            fieldRef.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
            break;
          }
        }
        this.$emit('updateNeedScroll');
      });
    },

    locateUser(userInput = true) {
      if (!userInput && (this.scannedIdentifier.latitude == null || this.scannedIdentifier.longitude == null)) {
        return
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            AddressUtils.convertLatLongToAddress(lat, lng, (address) => {
              this.address.value = address;
              this.address.state = true;
              this.address.invalidFeedback = null;
            });
          },
          () => {
          }
        );
      }
    },
    //endregion

    removeInvalidFeedback(field) {
      this.match[field]['state'] = null
    },
  },
}
</script>

<style scoped>
/* pour affichage desktop (md) : scroll pas sur la page entière mais sur le contenu modifiable uniquement */
@media (min-width: 769px) {
  #verify-match-step-right-part {
    height: 100%;
    overflow: auto;
  }

  #verify-match-step-right-part-inside {
    padding-left: 3px; /* pour voir le border sur les input focus */
    width: 100%;
    margin-bottom: auto; /* si il n'y a pas assez d'items pour remplir la hauteur */
  }
}

.choice-id-vehicule {
  display: flex;
  justify-content: center;
  color: var(--main-grey-medium);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.custom-append-locate {
  background-color: var(--main-black-light) !important;
  border: 1px solid var(--main-grey-dark) !important;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  padding-left: 1em;
  padding-right: 1em;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-append-locate-disabled {
  background-color: var(--main-black-medium) !important;
  border: 1px solid var(--main-grey-dark) !important;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  padding-left: 1em;
  padding-right: 1em;
  display: flex;
  justify-content: center;
  align-items: center;
}

.text-disabled {
  color: var(--main-black-darker);
}

</style>



// WEBPACK FOOTER //
// src/views/match/VerifyMatchStep2.vue